import { Box, IconButton, Typography, Menu, Button } from '@mui/material';
import { Search, TuneOutlined, MoreVert } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { NotificationByUser } from 'types/notification';
import { SystemNotificationItem } from './SystemNotificationItem';

interface NotificationPanelProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    notifications: NotificationByUser[];
    unreadCount: number;
    onMarkAsRead: (id: number) => void;
    onTogglePin?: (id: number) => void;
    onMute?: (id: number, module: string) => void;
    onCopyLink?: (link: string) => void;
    onUnreadUpdatesClick?: () => void;
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
    anchorEl,
    open,
    onClose,
    notifications,
    unreadCount,
    onMarkAsRead,
    onTogglePin,
    onMute,
    onCopyLink,
    onUnreadUpdatesClick
}) => {
    const theme = useTheme();

    return (
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            data-testid="notification-panel"
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
                elevation: 0,
                sx: {
                    minWidth: 540,
                    maxHeight: 600,
                    backgroundColor: theme.palette.primary[200],
                    borderRadius: '8px',
                    border: `1px solid ${theme.palette.neutralLightGrey.main}`,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }
            }}
        >
            <Box
                data-testid="notification-panel-header"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingX: '16px',
                    paddingTop: '12px',
                    backgroundColor: theme.palette.primary[200],
                    position: 'sticky',
                    top: 0,
                    zIndex: 1
                }}
            >
                <Typography
                    data-testid="notification-panel-title"
                    sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        fontSize: '20px',
                        letterSpacing: '0.15px'
                    }}
                >
                    Notification
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton size="small" sx={{ padding: '4px' }} data-testid="notification-panel-search-button">
                        <Search sx={{ color: theme.palette.secondary.main, fontSize: '20px' }} />
                    </IconButton>
                    <IconButton size="small" sx={{ padding: '4px' }} data-testid="notification-panel-filter-button">
                        <TuneOutlined sx={{ color: theme.palette.secondary.main, fontSize: '20px' }} />
                    </IconButton>
                    <IconButton size="small" sx={{ padding: '4px' }} data-testid="notification-panel-more-button">
                        <MoreVert sx={{ color: theme.palette.secondary.main, fontSize: '20px' }} />
                    </IconButton>
                </Box>
            </Box>

            <Box
                data-testid="notification-panel-subheader"
                sx={{
                    paddingX: '16px',
                    paddingY: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <Typography
                    data-testid="notification-panel-latest-label"
                    sx={{
                        color: theme.palette.secondary.main,
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '20px'
                    }}
                >
                    Latest Notification
                </Typography>
                <Button
                    onClick={onUnreadUpdatesClick}
                    data-testid="notification-panel-unread-updates-button"
                    sx={{
                        color: theme.palette.secondary.main,
                        fontWeight: 'bold',
                        fontSize: '14px',
                        lineHeight: '20px'
                    }}
                >
                    Unread Updates
                </Button>
            </Box>

            <Box
                data-testid="notification-panel-list"
                sx={{
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingX: '16px',
                    paddingY: '12px',
                    maxHeight: 'calc(600px - 120px)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    '&::-webkit-scrollbar': {
                        width: '8px'
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: theme.palette.grey[50]
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.neutralLightGrey.main,
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: theme.palette.grey[500]
                        }
                    }
                }}
            >
                {notifications.length > 0 ? (
                    notifications.map((notification) => (
                        <SystemNotificationItem
                            key={notification.message.id}
                            notification={notification}
                            onMarkAsRead={onMarkAsRead}
                            onTogglePin={onTogglePin}
                            onMute={onMute}
                            onCopyLink={onCopyLink}
                        />
                    ))
                ) : (
                    <Box sx={{ padding: '2rem', textAlign: 'center' }} data-testid="notification-panel-empty-state">
                        <Typography variant="body2" color="textSecondary" data-testid="notification-panel-empty-message">
                            No notifications
                        </Typography>
                    </Box>
                )}
            </Box>
        </Menu>
    );
};
